/**
 * 💙 Blue Mage Menu — David's Diner sub-page (Feb 2026, Phase 1).
 *
 * Read-only catalog for any class (lore visibility) + cook-and-eat unlock
 * action for class_id === "blue_mage" only. The Blue Mage class itself is
 * gated by the Vesper Acquisition Quest (Phase 3) — Phase 1 ships the menu
 * UI so the lore + roster is discoverable.
 *
 * Backend contract:
 *   GET  /api/blue-mage/skills       → { class_id, is_blue_mage, skills[], ... }
 *   POST /api/blue-mage/cook { skill_id } → { ok, already_learned, ... }
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlueMageMenuPage() {
  const { isAuthed } = useAuth();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(null);  // skill_id currently cooking

  const load = async () => {
    if (!isAuthed) return;
    try {
      const r = await axios.get(`${API}/blue-mage/skills`);
      setState(r.data);
    } catch {
      toast.error("Failed to load Blue Mage menu.");
    }
  };

  useEffect(() => { load(); }, [isAuthed]);

  const cook = async (skill_id) => {
    setBusy(skill_id);
    try {
      const r = await axios.post(`${API}/blue-mage/cook`, { skill_id });
      if (r.data?.already_learned) {
        toast(`${r.data.icon} ${r.data.name} — already learned.`);
      } else {
        toast.success(`💙 ${r.data.icon} ${r.data.name} learned!`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't learn that skill.");
    } finally {
      setBusy(null);
    }
  };

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#10001a] via-black to-[#1a0010] text-amber-100"
           data-testid="blue-mage-menu-logged-out">
        <Header />
        <main className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="text-6xl mb-3">💙</div>
          <h1 className="text-3xl font-black mb-2" style={{ fontFamily: "Anton, sans-serif" }}>
            BLUE MAGE MENU
          </h1>
          <p className="text-neutral-400 mb-6">Sign in to read Vesper's chalk menu.</p>
          <Link to="/auth" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("omni:open-auth")); }} className="inline-block px-5 py-2.5 rounded bg-amber-500 hover:bg-amber-400 text-black font-bold">
            Sign in
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (!state) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-amber-100"
           data-testid="blue-mage-menu-loading">
        <div className="animate-pulse text-xl">💙 Vesper is wiping the chalkboard...</div>
      </div>
    );
  }

  const isBM = state.is_blue_mage;
  const total = state.total_count;
  const unlocked = state.unlocked_count;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#10001a] via-black to-[#1a0010] text-amber-100"
         data-testid="blue-mage-menu-page">
      <Header />
      <main className="max-w-5xl mx-auto px-4 py-6 pb-24">

        {/* Hero */}
        <div className="rounded-2xl border-2 border-blue-500/40 bg-gradient-to-br from-[#06002a] via-black/60 to-[#10001a] p-6 mb-5 relative overflow-hidden">
          <div className="absolute -top-6 -right-6 text-[140px] opacity-10 select-none">💙</div>
          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.4em] text-blue-300/70 mb-1">
              Diner · Chalk Menu Under the Counter
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-blue-200 mb-2"
                style={{ fontFamily: "Anton, sans-serif" }}>
              💙 BLUE MAGE MENU
            </h1>
            <p className="text-sm md:text-base text-blue-100/80 max-w-2xl italic font-serif">
              "Every dish is a spell. Every bite is a memory. — Vesper the Eternal Crest"
            </p>
            <div className="mt-3 text-xs text-amber-200/70">
              Vesper teaches by feeding you. Each recipe permanently unlocks one Blue Mage skill — no respec.
            </div>
            <div className="mt-3 flex items-center gap-2 flex-wrap text-xs">
              <span className="px-2 py-1 rounded bg-blue-900/50 border border-blue-700/40 text-blue-200"
                    data-testid="bm-progress">
                {unlocked} / {total} learned
              </span>
              {isBM ? (
                <span className="px-2 py-1 rounded bg-emerald-900/40 border border-emerald-700/40 text-emerald-200"
                      data-testid="bm-class-badge">
                  💙 You are a Blue Mage
                </span>
              ) : (
                <span className="px-2 py-1 rounded bg-amber-900/30 border border-amber-700/40 text-amber-200"
                      data-testid="bm-class-locked">
                  🔒 Only Blue Mages can cook & eat. Complete the Vesper Acquisition Quest to unlock.
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Phase banner */}
        <div className="rounded-lg bg-yellow-900/20 border border-yellow-700/40 px-4 py-2.5 mb-5 text-[11px] text-yellow-200/80"
             data-testid="bm-phase-banner">
          ⚙️ <b>Phase 1 — menu & unlocks live.</b> Combat hookup (casting effects mid-fight) lands in Phase 2.
          Vesper companion AI + 💠 Astral Hatching summon land in Phase 3.
        </div>

        {/* Menu grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="bm-skills-grid">
          {(state.skills || []).map((s) => (
            <SkillCard key={s.id} s={s} isBM={isBM} busy={busy === s.id}
                       onCook={() => cook(s.id)} />
          ))}
        </div>

        {/* Footer link back */}
        <div className="mt-8 text-center">
          <Link to="/diner" className="text-xs text-amber-300 hover:text-amber-200 underline"
                data-testid="bm-back-to-diner">
            ← Back to Phoenix Flame Diner
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}


function SkillCard({ s, isBM, busy, onCook }) {
  const isProc = typeof s.proc_chance === "number";
  const tbdRecipe = s.recipe_name?.startsWith("TBD");

  // 🔒 LOCKED CANON (Feb 2026): Vesper's blue magic must be DISCOVERED.
  // Undiscovered skills render as a redacted mystery card — no name,
  // no recipe, no effect leak. Only the player's actions reveal them.
  if (s.mystery) {
    return (
      <div
        data-testid={`bm-skill-${s.id}`}
        className="rounded-xl border border-blue-900/40 bg-black/50 p-4 relative overflow-hidden hover:border-blue-700/60 transition"
      >
        <div className="absolute top-2 right-2 text-[9px] uppercase tracking-[0.3em] font-black text-blue-500/80"
             data-testid={`bm-mystery-${s.id}`}>
          🔒 Undiscovered
        </div>
        <div className="flex items-start gap-3">
          <div className="text-4xl flex-shrink-0 grayscale opacity-50">❓</div>
          <div className="flex-1 min-w-0">
            <div className="font-black text-base text-blue-300/60 italic" style={{ fontFamily: "Anton, sans-serif" }}>
              ???
            </div>
            <div className="mt-1 text-[11px] text-neutral-500 italic">
              Recipe and effect remain a mystery to Vesper&rsquo;s pupil.
            </div>
            <div className="mt-2 text-xs text-blue-200/70 font-serif leading-snug">
              <span className="text-blue-400/70 font-bold">✦</span> Discover at Dave&rsquo;s Diner — hunt the ingredient, cook the dish, eat to unlock.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={`bm-skill-${s.id}`}
      className={`rounded-xl border p-4 transition relative overflow-hidden ${
        s.unlocked
          ? "border-emerald-500/60 bg-gradient-to-br from-emerald-950/30 via-black/60 to-blue-950/30 shadow-[0_0_18px_rgba(16,185,129,0.18)]"
          : "border-blue-700/30 bg-black/40 hover:border-blue-500/60"
      }`}
    >
      {/* Unlocked stamp */}
      {s.unlocked && (
        <div className="absolute top-2 right-2 text-[9px] uppercase tracking-[0.3em] font-black text-emerald-300/90"
             data-testid={`bm-unlocked-${s.id}`}>
          ★ Learned
        </div>
      )}

      <div className="flex items-start gap-3">
        <div className="text-4xl flex-shrink-0">{s.icon}</div>
        <div className="flex-1 min-w-0">
          {/* Name row */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-black text-base text-blue-100" style={{ fontFamily: "Anton, sans-serif" }}>
              {s.name}
            </div>
            {isProc && (
              <span className="text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider bg-purple-900/40 border border-purple-700/40 text-purple-200"
                    data-testid={`bm-proc-${s.id}`}>
                🎲 {(s.proc_chance * 100).toFixed(0)}% proc
              </span>
            )}
            {s.boss_immune && (
              <span className="text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider bg-red-900/40 border border-red-700/40 text-red-200">
                🚫 boss-immune
              </span>
            )}
          </div>
          {/* Recipe + dish */}
          <div className="mt-1 text-[11px]">
            <div className={tbdRecipe ? "text-amber-400/80" : "text-amber-200/90"}>
              <span className="uppercase tracking-wider text-[9px] text-amber-400/60">Recipe:</span> {s.recipe_name}
            </div>
            <div className="text-neutral-400 italic font-serif">
              <span className="uppercase tracking-wider text-[9px] text-neutral-500 not-italic">Real dish:</span> {s.real_dish}
            </div>
          </div>
          {/* Effect */}
          <div className="mt-2 text-xs text-blue-100/90 font-serif leading-snug">
            <span className="text-blue-300 font-bold">✦</span> {s.effect}
          </div>
          {/* Notes */}
          {s.notes && (
            <div className="mt-1 text-[10px] text-neutral-500 italic">
              {s.notes}
            </div>
          )}

          {/* Cook button */}
          <div className="mt-3">
            {s.unlocked ? (
              <button
                disabled
                data-testid={`bm-cook-disabled-${s.id}`}
                className="text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded bg-emerald-900/40 border border-emerald-700/40 text-emerald-200 cursor-default"
              >
                ✓ Skill in your kit
              </button>
            ) : !isBM ? (
              <button
                disabled
                data-testid={`bm-cook-locked-${s.id}`}
                className="text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded bg-neutral-900 text-neutral-500 border border-neutral-700 cursor-not-allowed"
              >
                🔒 Blue Mage only
              </button>
            ) : tbdRecipe ? (
              <button
                disabled
                data-testid={`bm-cook-tbd-${s.id}`}
                title="Recipe ingredients pending owner spec — coming soon."
                className="text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded bg-yellow-900/30 border border-yellow-700/40 text-yellow-300 cursor-not-allowed"
              >
                ⏳ Recipe pending
              </button>
            ) : (
              <button
                disabled={busy}
                onClick={onCook}
                data-testid={`bm-cook-${s.id}`}
                className="text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white disabled:bg-neutral-800 disabled:text-neutral-500 transition"
              >
                {busy ? "Cooking..." : "🍳 Cook & Eat"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
